import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { Grid, Button, TextField, Typography, Autocomplete, IconButton } from '@mui/material';
import BookingFacilityCopy from './BookingFacilityCopy';
import { getBookingVendorAccountsData, postUpdateFacilityBookingType } from '../../../../api/facility';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { AwaitButton } from '../../../../reusable-components/await-button';
import BookingEmailTable from './BookingEmailTable';
import BookingAccountTable from './BookingAccountTable';
import LoadingAnimation from '../../../../reusable-components/loader/LoadingAnimation';
import { bookingTypeIsFacilitySeachable } from './BookingAccountSearchHelper';
import Iconify from '../../../../reusable-components/iconify';
import axiosInstance from '../../../../utils/axios';

BookingAccount.propTypes = {
  selectedRow: PropTypes.object,
  isSuperAdmin: PropTypes.bool
};

export default function BookingAccount({ selectedRow, isSuperAdmin }) {
  const { enqueueSnackbar } = useSnackbar();
  const [bookingData, setBookingData] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vendorBookingTypes, setVendorBookingTypes] = useState([]);
  const [facilityBookingTypes, setFacilityBookingType] = useState([]);
  const [apContacts, setAPContacts] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [bookingType, setBookingType] = useState(null);
  const [editBookingType, setEditBookingType] = useState(null);
  const [account, setAccount] = useState('');
  const [editAccount, setEditAccount] = useState('');
  const [subaccount, setSubaccount] = useState('');
  const [editSubaccount, setEditSubaccount] = useState('');  
  const [isLoading, setIsLoading] = useState(true);
  const [loadingBookingVendorFacilityData, setLoadingBookingVendorFacilityData] = useState(false);
  const [bookingVendorFacilitiesList, setBookingVendorFacilitiesList] = useState(null);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await getBookingVendorAccountsData(selectedRow.id);
      const { account, vendors, facilities, facilityBookingTypes, facilityBookingType, facilityVendorBookingAccounts,
        vendorBookingTypes, apContacts, subaccount } = response.data;
      setFacilities(facilities);
      setAPContacts(apContacts);
      setBookingData(facilityVendorBookingAccounts);
      setBookingType(facilityBookingType);
      setEditBookingType(facilityBookingType);
      setAccount(account || '');
      setEditAccount(account || '');
      setSubaccount(subaccount || '');
      setEditSubaccount(subaccount || '');
      setVendors(vendors);
      setFacilityBookingType(facilityBookingTypes);
      setVendorBookingTypes(vendorBookingTypes);
    } catch (error) {
      enqueueSnackbar('Failed to load data', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [selectedRow]);
 
  const handleSave = async () => {
    try {
      const response = await postUpdateFacilityBookingType(selectedRow.id, editBookingType?.id, editAccount, editSubaccount);
      const success = response?.data?.success;
      enqueueSnackbar(success ? 'Booking type updated successfully' : response.data?.message || "Something went wrong", { variant: success ? 'success' : 'error' });
      if (success) fetchData();
    } catch (error) {
      enqueueSnackbar("Something went wrong", { variant: 'error' });
    }
  };

  const fetchBookingVendorFacilityAccounts = () => {
    setLoadingBookingVendorFacilityData(true);
    axiosInstance.get(`Admin/Facility/SearchBookingVendorFacilities?facility=${encodeURIComponent(editAccount)}&bookingTypeId=${editBookingType?.id}`)
      .then(res => {
        if (res.data?.length) setBookingVendorFacilitiesList(res.data.map(data => ({ ...data, label: `${data.label} (${data.value})` })));
        else enqueueSnackbar('No facilities found', { variant: 'error' });
      })
      .catch(() => enqueueSnackbar('Failed to fetch booking facility data', { variant: 'error' }))
      .finally(() => setLoadingBookingVendorFacilityData(false));
  }

  const edit = bookingType !== editBookingType || account !== editAccount || subaccount !== editSubaccount;

  return isLoading ? <LoadingAnimation /> : <>
    <Grid item xs={12}>
      <Grid container direction="row" justifyContent="flex-start" alignItems="flex-end" sx={{ mt: 2 }} spacing={2}>
        <Grid item>
          <Typography variant="subtitle1">Booking Type</Typography>
          <Autocomplete
            fullWidth
            value={editBookingType}
            onChange={(event, newValue) => setEditBookingType(newValue)}
            size="small"
            options={facilityBookingTypes}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderInput={(params) => <TextField {...params} label="Search or select" autoComplete="off" />}
            disabled={!isSuperAdmin}
            sx={{
              width: '300px',
              '& legend': { display: 'none' },
              '& fieldset': { top: 0 },
              '& .MuiFormLabel-root ': { display: 'none' },
            }}
          />
        </Grid>

        {editBookingType?.useFacilityAccount && <Grid item>
          <Typography variant="subtitle1">Booking Account #</Typography>          
          {bookingVendorFacilitiesList?.length > 0 ?
            <Autocomplete
              fullWidth
              onChange={(_, newValue) => {
                if (newValue?.value) setEditAccount(newValue.value);
                setBookingVendorFacilitiesList(null);
              }}
              size="small"
              defaultValue={editAccount}
              options={bookingVendorFacilitiesList}
              isOptionEqualToValue={(option, value) => option?.value === value}
              autoFocus
              openOnFocus
              renderInput={(params) => <TextField {...params} label="Search or select" autoComplete="off" autoFocus />}
              sx={{
                width: '300px',
                '& legend': { display: 'none' },
                '& fieldset': { top: 0 },
                '& .MuiFormLabel-root ': { display: 'none' },
              }}
            />
            : <TextField size="small" sx={{ width: '250px' }} value={editAccount} disabled={!isSuperAdmin} onChange={(e) => setEditAccount(e.target.value)} autoComplete="off"
              InputProps={{
                endAdornment: isSuperAdmin ? <IconButton
                  
                  color="secondary"
                  disabled={loadingBookingVendorFacilityData}
                  disableRipple
                  sx={{ display: bookingTypeIsFacilitySeachable(editBookingType) ? 'inline-flex' : 'none', p: 0, mr: -.5 }}
                  onClick={fetchBookingVendorFacilityAccounts}
                >
                  <Iconify icon={'fa6-brands:searchengin'} sx={{ cursor: 'pointer' }} />
                </IconButton> : null
              }}
            />}        
        </Grid>}

        {editBookingType?.useFacilitySubaccount && <Grid item>
          <Typography variant="subtitle1">Sub Account #</Typography>
          <TextField size="small" sx={{ width: '250px' }} disabled={!isSuperAdmin} value={editSubaccount} onChange={(e) => setEditSubaccount(e.target.value)} autoComplete="off" />
        </Grid>}
      </Grid>
      <Grid container direction="row" justifyContent="flex-start" alignItems="flex-end" sx={{ mt: 2 }}>
        {!edit && isSuperAdmin && <BookingFacilityCopy bookingType={bookingType} facilities={facilities} facilityRow={selectedRow} fetchData={fetchData} />}
        {edit && <>
          <Button
            size="small"
            sx={{ mr: 1 }}
            variant="outlined"
            color="error"
            onClick={() => { setEditAccount(account); setEditBookingType(bookingType); setEditSubaccount(subaccount); }}
          >
            Cancel
          </Button>
          <AwaitButton size="small" disabled={!edit} variant="outlined" onClick={handleSave}>
            Save
          </AwaitButton>
        </>}
      </Grid>
      {(editBookingType?.useFacilityAccount || editBookingType?.useVendorAccounts) && <BookingAccountTable
        bookingVendorAccounts={bookingData}
        facilityRow={selectedRow}
        isSuperAdmin={isSuperAdmin}
        isLoading={isLoading}
        vendors={vendors}
        vendorBookingTypes={vendorBookingTypes}
        fetchData={fetchData}
        disabled={edit}
        bookingType={editBookingType}
      />}
      {editBookingType?.useEmail && <BookingEmailTable
        apContacts={apContacts}
        facilityRow={selectedRow}
        fetchData={fetchData}
        isLoading={isLoading}
        isSuperAdmin={isSuperAdmin}
        disabled={edit}
        bookingType={editBookingType}
      />}
    </Grid>
  </>;
}
